import { api, request } from '../api/client'
import type { components } from '../api/generated/schema'
import { clearTokens, getRefreshToken, setTokens } from './tokenStore'
import { toAuthTokens, toCurrentUser, type AuthTokens, type CurrentUser } from './types'

type TokenResponseDto = components['schemas']['TokenResponse']
type MeResponseDto = components['schemas']['MeResponse']

/** Dev-only extra: the API returns the confirmation link so the flow is testable without an inbox. */
interface RegisterResponse {
  message: string
  confirmationLink?: string
}

export async function login(email: string, password: string): Promise<AuthTokens> {
  // anonymous: no bearer, and — importantly — no refresh-and-retry. A 401 here
  // means "wrong password", and retrying it with a fresh token would be nonsense.
  const dto = await request<TokenResponseDto>('/auth/login', {
    method: 'POST',
    body: { email, password },
    anonymous: true,
  })

  const tokens = toAuthTokens(dto)
  setTokens(tokens)
  return tokens
}

export async function register(
  email: string,
  password: string,
  displayName: string | null,
): Promise<RegisterResponse> {
  return request<RegisterResponse>('/auth/register', {
    method: 'POST',
    body: { email, password, displayName },
    anonymous: true,
  })
}

export async function confirmEmail(userId: string, code: string): Promise<string> {
  const result = await request<{ message: string }>('/auth/confirm-email', {
    method: 'GET',
    query: { userId, code },
    anonymous: true,
  })
  return result.message
}

export async function fetchMe(): Promise<CurrentUser> {
  return toCurrentUser(await api.get<MeResponseDto>('/me'))
}

/**
 * Ends the session. The server call rotates the Identity security stamp, which
 * invalidates every outstanding refresh token for this account — so logging out
 * here logs the account out on **every device**. Surface that in the UI; it
 * surprises people otherwise.
 *
 * Local tokens are cleared whether or not the server call succeeds: a failed
 * logout must never leave the user looking signed in.
 */
export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout')
  } catch {
    // Offline, or the token had already expired. Either way, clear locally.
  } finally {
    clearTokens()
  }
}

// ---------------------------------------------------------------------------
// Refresh — single-flight.
// ---------------------------------------------------------------------------

let inFlightRefresh: Promise<boolean> | null = null

/**
 * Exchanges the refresh token for a new pair, at most once at a time.
 *
 * The single-flight guard is the whole point. A screen that fires five queries
 * on mount gets five simultaneous 401s the moment the access token expires;
 * without this, that is five concurrent refreshes racing, and because the API
 * *rotates* the refresh token on every use, four of them would be redeeming a
 * token that a sibling had already replaced — logging the user out at random.
 * They all await one call instead.
 *
 * Returns whether a usable access token is now available. Never throws: it is
 * wired in as the API client's `onUnauthorized`, where a rejection would mask
 * the original request's error.
 */
export function refreshSession(): Promise<boolean> {
  inFlightRefresh ??= runRefresh().finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}

async function runRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (refreshToken === null || refreshToken.length === 0) {
    return false
  }

  try {
    const dto = await request<TokenResponseDto>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      anonymous: true,
    })
    setTokens(toAuthTokens(dto))
    return true
  } catch {
    // Expired, already rotated, or revoked by a logout elsewhere. The session is
    // over; drop the tokens so the guard routes to login instead of looping.
    clearTokens()
    return false
  }
}

/** Test seam: drops any pending refresh so tests do not share one across cases. */
export function resetRefreshState(): void {
  inFlightRefresh = null
}
